import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { LanguageSwitcher, useT } from "@/lib/i18n";
import { useProfileT } from "@/lib/profile-i18n";
import { supabase } from "@/integrations/supabase/client";
import { BackButton } from "@/components/BackButton";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { ManualNotificationsBell } from "@/components/ManualNotificationsBell";
import logoAsset from "@/assets/logo.png.asset.json";

export function SiteHeader() {
  const { user, hasRole } = useAuth();
  const { t } = useT();
  const pt = useProfileT();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);

  // Admin section has its own shell.
  if (pathname.startsWith("/admin")) return null;

  const isAdmin = hasRole("admin");
  const isStaff = isAdmin || hasRole("moderator");
  const isVakantOwner = hasRole("vakant_owner");

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setOpen(false);
    navigate({ to: "/" });
  };

  const linkCls =
    "text-sm font-medium text-muted-foreground transition-colors hover:text-brand-terracotta";
  const mobileLinkCls =
    "block rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-brand-cream/60";

  const close = () => setOpen(false);

  const navItems = (
    <>
      <Link to="/" className={linkCls} onClick={close}>{t("nav.home")}</Link>
      <Link to="/mapa" className={linkCls} onClick={close}>{t("nav.mapa")}</Link>
      {user ? (
        <>
          <Link to="/profil" className={linkCls} onClick={close}>{pt("title")}</Link>
          <Link to="/moje-podniky" className={linkCls} onClick={close}>{t("nav.mojePodniky")}</Link>

          <Link to="/registrace-podniku" className={linkCls} onClick={close}>{t("nav.proMajitele")}</Link>
          {isVakantOwner && (
            <Link to="/majitele-vacantu" className={linkCls} onClick={close}>{t("nav.vakanty")}</Link>
          )}
          {isStaff && (
            <Link to="/admin" className="text-sm font-semibold text-brand-earth hover:text-brand-terracotta" onClick={close}>
              {t("nav.admin")}
            </Link>
          )}
        </>
      ) : (
        <Link to="/registrace-podniku" className={linkCls} onClick={close}>{t("nav.proMajitele")}</Link>
      )}
    </>
  );

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-brand-earth/10 bg-brand-cream/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <img
              src={logoAsset.url}
              alt="Cerveza.International"
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
            <span className="hidden truncate font-serif text-xl font-bold tracking-tight text-brand-earth sm:inline">
              Cerveza<span className="text-brand-terracotta">.</span>International
            </span>
          </Link>

          <div className="hidden flex-wrap items-center gap-5 lg:flex">
            {navItems}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher />
            {user && <ManualNotificationsBell />}
            {user ? (
              <button
                onClick={handleLogout}
                className="hidden rounded-full bg-brand-earth px-4 py-2 text-xs font-semibold text-brand-cream transition-all hover:bg-brand-terracotta lg:inline-flex"
              >
                {t("nav.odhlasit")}
              </button>
            ) : (
              <Link
                to="/auth"
                className="hidden rounded-full bg-brand-earth px-4 py-2 text-xs font-semibold text-brand-cream transition-all hover:bg-brand-terracotta lg:inline-flex"
              >
                {t("nav.prihlasit")}
              </Link>
            )}

            {/* Hamburger – mobile & tablet */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Menu"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-earth hover:bg-brand-cream/60 lg:hidden"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-brand-cream">
                <SheetHeader>
                  <SheetTitle className="font-serif text-brand-earth">
                    Cerveza<span className="text-brand-terracotta">.</span>International
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-1 [&_a]:!text-base [&_a]:!block [&_a]:!rounded-md [&_a]:!px-3 [&_a]:!py-3 [&_a]:!text-foreground [&_a]:hover:!bg-brand-earth/5">
                  {navItems}
                </div>
                <div className="mt-6 border-t border-brand-earth/10 pt-4">
                  {user ? (
                    <button
                      onClick={handleLogout}
                      className="w-full rounded-md bg-brand-earth px-4 py-3 text-sm font-semibold text-brand-cream"
                    >
                      {t("nav.odhlasit")}
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={close}
                      className={mobileLinkCls + " !bg-brand-earth !text-brand-cream text-center"}
                    >
                      {t("nav.prihlasit")}
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
      {pathname !== "/" && (
        <div className="mx-auto max-w-7xl px-3 pt-3 sm:px-6">
          <BackButton />
        </div>
      )}
    </>
  );
}
